import { format, parse, startOfWeek, getDay, add, isBefore  } from "date-fns";
import { enUS, es, it, pt } from "date-fns/locale";

import { useEffect, useState } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import Card from "components/UI/Card/Card";
import CardBody from "components/UI/Card/CardBody";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { useSubscription } from "store/auth/auth-store";
import { useGetMonitorData } from "services/input-core/monitor/get-monitor";
import { MonitorData } from "services/input-core/monitor/@types";
import { CircularProgress } from "@mui/material";

interface Event {
  title: string;
  allDay: boolean;
  start: Date;
  end: Date;
  color: string;
}

const locales = { en: enUS, es, it, pt };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});


const eventColors = (event: Event) => {
  const backgroundColor = event.color
    ? `event-${event.color}`
    : "event-default";
  return {
    className: backgroundColor,
  };
};

export default function Monitor() {
  const [events, setEvents] = useState<Event[]>([]);
  const { t, i18n } = useTranslation("dashboard");
  const [culture, setCulture] = useState(i18n?.language);
  const { subscription } = useSubscription();
  const {
    data: monitorEntries,
    isLoading: isLoadingMonitorData,
  } = useGetMonitorData();

  useEffect(() => {
    if (subscription && monitorEntries) {
      loadExpirationMonitor();
    }
  }, [subscription, monitorEntries]);

  useEffect(() => {
    const onLanguageChange = (newLanguage: string) => {
      setCulture(newLanguage);
    };

    const languageChangeHandler = () => {
      const newLanguage = i18n.language;
      onLanguageChange(newLanguage);
    };

    i18n.on("languageChanged", languageChangeHandler);

    return () => {
      i18n.off("languageChanged", languageChangeHandler);
    };
  }, [i18n]);

  const loadExpirationMonitor = () => {
    const calendaryEvents = monitorEntries?.map((data: MonitorData) => {
      const formattedDate = add(new Date(data.start), { hours: 3 });

      return {
        title: `${data.documentType} - ${data.requestParam}`,
        allDay: true,
        start: formattedDate,
        end: formattedDate,
        color:  formattedDate && isBefore(formattedDate, new Date()) ? "red" : "green",
      };
    });

    const validDates = calendaryEvents?.filter(
      (event) => event.color === "green"
    );
    setEvents(validDates);
  };

  return (
    <Card>
      <CardBody calendar={!!events?.length}>
        {isLoadingMonitorData ? (
          <CircularProgress />
        ) : !events?.length ? (
          <h5>{t("no-documents")}</h5>
        ) : (
          <BigCalendar
            selectable
            localizer={localizer}
            culture={culture}
            events={events}
            defaultView='month'
            scrollToTime={new Date(2020, 1, 1, 6)}
            defaultDate={new Date()}
            eventPropGetter={eventColors}
          />
        )}
      </CardBody>
    </Card>
  );
}
